// Utility to map Airtable records to Client format

import {
  Client,
  AirtableRecord,
  DiscussedItem,
  SkincareQuizData,
  AnalysisSeverityScoresData,
  WellnessQuizCategoryScore,
  WellnessQuizData,
} from "../types";
import { coerceToAirtableNumberAge } from "./validation";
import { parseProcessedFocusAreas } from "./parseFocusAreas";

/** Parse "Skincare Quiz" long text field (JSON) from Airtable fields. Exported for use when fetching quiz fields separately. */
export function parseSkincareQuizFromFields(fields: Record<string, unknown>): SkincareQuizData | undefined {
  const raw = (fields["Skincare Quiz"] ?? fields["Skincare quiz"] ?? null) as string | null;
  if (!raw || typeof raw !== "string" || !raw.trim()) return undefined;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return undefined;
    const o = parsed as Record<string, unknown>;
    if (o.version !== 1 || typeof o.completedAt !== "string") return undefined;
    if (!o.answers || typeof o.answers !== "object") return undefined;
    const validResults: string[] = ["opal", "pearl", "jade", "quartz", "amber", "moonstone", "turquoise", "diamond"];
    if (typeof o.result !== "string" || !validResults.includes(o.result)) return undefined;
    return {
      version: 1 as const,
      completedAt: o.completedAt,
      answers: o.answers as Record<string, number>,
      result: o.result as SkincareQuizData["result"],
      recommendedProductNames: Array.isArray(o.recommendedProductNames)
        ? o.recommendedProductNames.filter((x): x is string => typeof x === "string")
        : undefined,
      resultLabel: typeof o.resultLabel === "string" ? o.resultLabel : undefined,
      resultDescription: typeof o.resultDescription === "string" ? o.resultDescription : undefined,
    };
  } catch {
    return undefined;
  }
}

function parseWellnessQuizCategoryScores(
  raw: unknown,
): WellnessQuizCategoryScore[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: WellnessQuizCategoryScore[] = [];
  for (const x of raw) {
    if (!x || typeof x !== "object") continue;
    const r = x as Record<string, unknown>;
    if (
      typeof r.id === "string" &&
      typeof r.label === "string" &&
      typeof r.percent === "number" &&
      Number.isFinite(r.percent) &&
      typeof r.raw === "number" &&
      Number.isFinite(r.raw)
    ) {
      out.push({
        id: r.id,
        label: r.label,
        percent: r.percent,
        raw: r.raw,
      });
    }
  }
  return out.length > 0 ? out : undefined;
}

/**
 * Parse "Wellness Quiz" long text JSON from Airtable (v1 legacy or v2 with severity + categoryScores).
 * Exported for API / standalone results validation.
 */
export function parseWellnessQuizJson(raw: string): WellnessQuizData | undefined {
  const t = raw.trim();
  if (!t) return undefined;
  try {
    const parsed = JSON.parse(t) as unknown;
    if (!parsed || typeof parsed !== "object") return undefined;
    const o = parsed as Record<string, unknown>;
    if (typeof o.completedAt !== "string") return undefined;
    if (!o.answers || typeof o.answers !== "object" || Array.isArray(o.answers)) {
      return undefined;
    }

    const ver = o.version;
    const version: 1 | 2 = ver === 2 ? 2 : 1;

    const ids = Array.isArray(o.suggestedTreatmentIds)
      ? o.suggestedTreatmentIds.filter((x): x is string => typeof x === "string")
      : [];

    const categoryScores = parseWellnessQuizCategoryScores(o.categoryScores);

    const base: WellnessQuizData = {
      version,
      completedAt: o.completedAt,
      answers: o.answers as WellnessQuizData["answers"],
      suggestedTreatmentIds: ids,
    };
    if (categoryScores && categoryScores.length > 0) {
      base.categoryScores = categoryScores;
    }
    return base;
  } catch {
    return undefined;
  }
}

/** Read Wellness Quiz field from Airtable field map (supports alternate field name casing). */
export function parseWellnessQuizFromFields(
  fields: Record<string, unknown>,
): WellnessQuizData | undefined {
  const raw = fields["Wellness Quiz"] ?? fields["Wellness quiz"] ?? null;
  if (!raw || typeof raw !== "string" || !raw.trim()) return undefined;
  return parseWellnessQuizJson(raw);
}

function parseSeverityScoresFromFields(
  fields: Record<string, unknown>,
): AnalysisSeverityScoresData | undefined {
  const raw = fields["Severity Scores (from Analyses)"] ?? null;
  if (!raw) return undefined;

  const normalizedRaw: unknown = Array.isArray(raw) ? raw[0] : raw;
  if (!normalizedRaw) return undefined;

  const parsed = (() => {
    if (typeof normalizedRaw === "string") {
      const t = normalizedRaw.trim();
      if (!t) return null;
      try {
        return JSON.parse(t) as unknown;
      } catch {
        return null;
      }
    }
    if (typeof normalizedRaw === "object") return normalizedRaw as unknown;
    return null;
  })();
  if (!parsed || typeof parsed !== "object") return undefined;
  const obj = parsed as Record<string, unknown>;
  if (!obj.issues || typeof obj.issues !== "object" || Array.isArray(obj.issues)) {
    return undefined;
  }
  const normalizedIssues: AnalysisSeverityScoresData["issues"] = {};
  for (const [issueName, issueData] of Object.entries(
    obj.issues as Record<string, unknown>,
  )) {
    if (!issueData || typeof issueData !== "object") continue;
    const issue = issueData as Record<string, unknown>;
    const rawNorm01 =
      typeof issue.severity_normalized_0_1 === "number"
        ? issue.severity_normalized_0_1
        : typeof issue.severityNormalized01 === "number"
          ? issue.severityNormalized01
          : typeof issue.severityNormalized0_1 === "number"
            ? issue.severityNormalized0_1
            : undefined;
    normalizedIssues[issueName] = {
      predicted: issue.predicted === true,
      probability:
        typeof issue.probability === "number" && Number.isFinite(issue.probability)
          ? issue.probability
          : undefined,
      severity:
        typeof issue.severity === "number" && Number.isFinite(issue.severity)
          ? issue.severity
          : undefined,
      severity_normalized_0_1:
        typeof rawNorm01 === "number" && Number.isFinite(rawNorm01)
          ? Math.max(0, Math.min(1, rawNorm01))
          : undefined,
      severity_level:
        typeof issue.severity_level === "string" ? issue.severity_level : undefined,
      source: typeof issue.source === "string" ? issue.source : undefined,
      model_used:
        typeof issue.model_used === "string" ? issue.model_used : undefined,
    };
  }
  if (Object.keys(normalizedIssues).length === 0) return undefined;
  return {
    schema_version:
      typeof obj.schema_version === "number" && Number.isFinite(obj.schema_version)
        ? obj.schema_version
        : undefined,
    detector_type:
      typeof obj.detector_type === "string" ? obj.detector_type : undefined,
    submission_id: typeof obj.submission_id === "string" ? obj.submission_id : undefined,
    issues: normalizedIssues,
  };
}

/** Normalize Airtable "Wellness Goals": long text (comma / JSON array), or multi-select string array. */
export function parseWellnessGoalsFromAirtable(value: unknown): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value
      .map((x) => String(x).trim())
      .filter(Boolean);
  }
  if (typeof value === "string") {
    const t = value.trim();
    if (!t) return [];
    try {
      const p = JSON.parse(t) as unknown;
      if (Array.isArray(p)) {
        return p
          .filter((x) => x != null && String(x).trim())
          .map((x) => String(x).trim());
      }
    } catch {
      /* plain comma-separated text */
    }
    return t
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

/**
 * Map Airtable status field to dashboard status
 */
function mapAirtableStatus(
  fields: Record<string, any>,
): "new" | "contacted" | "requested-consult" | "scheduled" | "converted" | "current-client" {
  const status = (fields["Status"] || "").toLowerCase();

  if (
    status.includes("current client") ||
    status.includes("current patient") ||
    status.includes("current-client") ||
    status.includes("current-patient")
  ) {
    return "current-client";
  }
  if (
    status.includes("converted") ||
    status.includes("booked") ||
    status.includes("patient")
  ) {
    return "converted";
  }
  if (status.includes("scheduled") || status.includes("consultation")) {
    return "scheduled";
  }
  if (status.includes("requested") && status.includes("consult")) {
    return "requested-consult";
  }
  if (status.includes("contacted") || status.includes("reached")) {
    return "contacted";
  }
  return "new";
}

/**
 * Format patient source value for display
 */
function formatPatientSource(sourceValue: string | null | undefined): string {
  // Handle null, undefined, or empty string
  if (
    !sourceValue ||
    (typeof sourceValue === "string" && sourceValue.trim() === "")
  ) {
    return "Patient";
  }

  const source = String(sourceValue).toLowerCase().trim();

  // Map source values to display names
  if (source === "web" || source === "website") {
    return "Facial Analysis Form";
  } else if (
    source === "treatment qr" ||
    (source.includes("treatment") && source.includes("qr"))
  ) {
    return "Treatment Room QR Code";
  } else if (source === "qr" || source.includes("qr code")) {
    return "QR Code";
  }

  // Return original value with proper capitalization if no match
  const str = String(sourceValue);
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/** Sentinel value for Web Popup Leads with no facial analysis; display as "—" instead of "Pending". */
export const WEB_POPUP_LEAD_NO_ANALYSIS_STATUS = "n/a";

/**
 * Get facial analysis status from record fields.
 * Patients table may use "Pending/Opened" or other names; Web Popup Leads typically don't have this field.
 * For Web Popup Leads with no status field, returns WEB_POPUP_LEAD_NO_ANALYSIS_STATUS so the UI shows "—" instead of "Pending".
 */
function getFacialAnalysisStatus(
  fields: Record<string, any>,
  tableName: string,
): string | null {
  if (tableName === "Patients") {
    const candidates = [
      "Pending/Opened",
      "Facial Analysis Status",
      "Analysis Status",
      "Pending Opened",
      "Status (from Analyses)",
    ];
    for (const key of candidates) {
      const v = fields[key];
      if (v != null && String(v).trim() !== "") return String(v).trim();
    }
  }
  const raw = fields["Pending/Opened"];
  if (raw != null && String(raw).trim() !== "") return String(raw).trim();
  if (tableName === "Web Popup Leads") return WEB_POPUP_LEAD_NO_ANALYSIS_STATUS;
  return null;
}

/** Best-effort last-activity instant from lead/patient row (before contact-history merge). */
function lastContactFromAirtableFields(fields: Record<string, any>): string | null {
  const raw = fields["Last Contact"] ?? fields["Contacted"];
  if (raw == null || raw === "") return null;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t) return null;
    const ms = new Date(t).getTime();
    return Number.isFinite(ms) ? new Date(ms).toISOString() : t;
  }
  return null;
}

/**
 * Determine priority based on fields
 */
function determinePriority(
  fields: Record<string, any>,
): "high" | "medium" | "low" {
  // High priority: recent activity, high engagement, or scheduled
  const lastContact = fields["Last Contact"] || fields["Contacted"];
  const photosViewed = parseInt(fields["Photos Viewed"]) || 0;
  const status = (fields["Status"] || "").toLowerCase();

  if (status.includes("scheduled") || status.includes("consultation")) {
    return "high";
  }
  if (status.includes("requested") && status.includes("consult")) {
    return "high";
  }

  if (
    photosViewed > 5 ||
    (lastContact &&
      new Date(lastContact) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000))
  ) {
    return "high";
  }

  if (photosViewed > 0 || lastContact) {
    return "medium";
  }

  return "low";
}

/**
 * Map Airtable record to Client format
 */
export function mapRecordToClient(
  record: AirtableRecord,
  tableName: string,
): Client {
  const fields = record.fields || {};
  const patientWellnessGoals =
    tableName === "Patients"
      ? parseWellnessGoalsFromAirtable(fields["Wellness Goals"])
      : [];

  const client: Client = {
    id: record.id,
    name: fields["Name"] || "",
    email:
      tableName === "Patients"
        ? fields["Email"] || ""
        : fields["Email Address"] || "",
    phone:
      tableName === "Patients"
        ? fields["Patient Phone Number"] || fields["Phone Number"] || ""
        : fields["Phone Number"] || "",
    zipCode:
      fields["Zip Code"] || fields["Zip"] || fields["Postal Code"] || null,
    age:
      tableName === "Patients"
        ? coerceToAirtableNumberAge(fields["Age (from Form Submissions)"]) ??
          coerceToAirtableNumberAge(fields["Age"])
        : coerceToAirtableNumberAge(fields["Age"]),
    ageRange: tableName === "Patients" ? null : fields["Age Range"] || null,
    dateOfBirth:
      tableName === "Patients"
        ? fields["Birthday (from Form Submissions)"] || null
        : fields["Date of Birth"] || null,
    goals:
      tableName === "Patients"
        ? (() => {
            const fromItems = fields["Name (from Interest Items)"];
            let base: string[] = [];
            if (Array.isArray(fromItems) && fromItems.length) {
              base = fromItems.map((x) => String(x));
            } else if (typeof fromItems === "string" && fromItems.trim()) {
              base = fromItems
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
            } else {
              const goalsField = fields["Goals"];
              if (Array.isArray(goalsField) && goalsField.length) {
                base = goalsField.map((x) => String(x));
              }
            }
            if (patientWellnessGoals.length === 0) return base;
            const seen = new Set(base.map((s) => s.trim().toLowerCase()));
            const out = [...base];
            for (const w of patientWellnessGoals) {
              const k = w.trim().toLowerCase();
              if (!k || seen.has(k)) continue;
              seen.add(k);
              out.push(w);
            }
            return out;
          })()
        : Array.isArray(fields["Goals"])
          ? fields["Goals"]
          : [],
    wellnessGoals: patientWellnessGoals,
    concerns:
      tableName === "Patients"
        ? fields["Areas of Interest (from Form Submissions)"] ||
          (Array.isArray(
            fields[
              "Which regions of your face do you want to improve? (from Form Submissions)"
            ],
          )
            ? fields[
                "Which regions of your face do you want to improve? (from Form Submissions)"
              ].join(", ")
            : "") ||
          ""
        : Array.isArray(fields["Concerns"])
          ? fields["Concerns"]
          : fields["Concerns"] || "",
    areas:
      tableName === "Patients"
        ? null
        : Array.isArray(fields["Areas"])
          ? fields["Areas"]
          : null,
    aestheticGoals: (() => {
      let value =
        tableName === "Patients"
          ? fields["What would you like to improve? (from Form Submissions)"] ||
            fields["Aesthetic Goals"] ||
            fields["Notes"] ||
            ""
          : fields["Aesthetic Goals"] || fields["Notes"] || "";
      if (Array.isArray(value)) {
        return value.join(", ");
      }
      return value || "";
    })(),
    skinType: tableName === "Patients" ? null : fields["Skin Type"] || null,
    skinTone: tableName === "Patients" ? null : fields["Skin Tone"] || null,
    ethnicBackground:
      tableName === "Patients" ? null : fields["Ethnic Background"] || null,
    engagementLevel:
      tableName === "Patients" ? null : fields["Engagement Level"] || null,
    casesViewedCount:
      tableName === "Patients" ? null : fields["Cases Viewed Count"] || null,
    totalCasesAvailable:
      tableName === "Patients" ? null : fields["Total Cases Available"] || null,
    concernsExplored:
      tableName === "Patients"
        ? null
        : Array.isArray(fields["Concerns Explored"])
          ? fields["Concerns Explored"]
          : null,
    photosLiked: Array.isArray(fields["Liked Photos"])
      ? fields["Liked Photos"].length
      : 0,
    photosViewed:
      tableName === "Patients"
        ? parseInt(fields["Photos Viewed"]) ||
          parseInt(fields["Interested Photos Viewed"]) ||
          0
        : Array.isArray(fields["Viewed Photos"])
          ? fields["Viewed Photos"].length
          : 0,
    treatmentsViewed: [],
    source: (() => {
      const sourceValue =
        fields["Source"] || fields["source"] || fields["SOURCE"] || "";
      const raw = String(sourceValue).trim();
      if (tableName === "Web Popup Leads") {
        if (!raw) return "Website";
        return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
      } else if (tableName === "Patients") {
        return formatPatientSource(raw || undefined);
      } else {
        return raw || "AI Consult";
      }
    })(),
    status: mapAirtableStatus(fields),
    priority: determinePriority(fields),
    createdAt: record.createdTime || new Date().toISOString(),
    notes: fields["Notes"] || "",
    appointmentDate: fields["Appointment Date"] || null,
    treatmentReceived: fields["Treatment Received"] || null,
    revenue: fields["Revenue"] || null,
    lastContact: lastContactFromAirtableFields(fields),
    isReal: true,
    tableSource: tableName as "Web Popup Leads" | "Patients",
    facialAnalysisStatus: getFacialAnalysisStatus(fields, tableName),
    frontPhoto: (() => {
      // Only Patients table has Front Photo field
      // Note: frontPhoto is typed as string | null but actually contains attachment array
      // We'll store it as the array directly (type casting to match interface)
      if (tableName === "Patients") {
        const frontPhoto =
          fields["Front Photo"] ||
          fields["Front photo"] ||
          fields["frontPhoto"];
        if (frontPhoto && Array.isArray(frontPhoto) && frontPhoto.length > 0) {
          return frontPhoto as any; // Store as array, cast to match string | null type
        }
      }
      return null;
    })(),
    frontPhotoLoaded: false,
    allIssues:
      tableName === "Patients"
        ? fields["Name (from All Issues) (from Analyses)"] || ""
        : "",
    interestedIssues:
      tableName === "Patients"
        ? (() => {
            const fromInterestItems = Array.isArray(
              fields["Name (from Interest Items)"],
            )
              ? fields["Name (from Interest Items)"].join(", ")
              : fields["Name (from Interest Items)"] || "";
            if (String(fromInterestItems).trim()) {
              return String(fromInterestItems).trim();
            }
            /** Free-text wellness intake (comma-separated) → "Goals from intake" chips after partition */
            const fromWellnessText = (v: unknown): string => {
              if (typeof v === "string" && v.trim()) return v.trim();
              if (Array.isArray(v) && v.length)
                return v.filter(Boolean).join(", ");
              return "";
            };
            return (
              fromWellnessText(fields["Aesthetic Goals"]) ||
              fromWellnessText(fields["Wellness Intake Goals"]) ||
              ""
            );
          })()
        : "",
    whichRegions:
      tableName === "Patients"
        ? Array.isArray(
            fields[
              "Which regions of your face do you want to improve? (from Form Submissions)"
            ],
          )
          ? fields[
              "Which regions of your face do you want to improve? (from Form Submissions)"
            ].join(", ")
          : fields[
              "Which regions of your face do you want to improve? (from Form Submissions)"
            ] || ""
        : "",
    skinComplaints:
      tableName === "Patients"
        ? Array.isArray(
            fields["Do you have any skin complaints? (from Form Submissions)"],
          )
          ? fields[
              "Do you have any skin complaints? (from Form Submissions)"
            ].join(", ")
          : fields[
              "Do you have any skin complaints? (from Form Submissions)"
            ] || ""
        : "",
    processedAreasOfInterest:
      tableName === "Patients"
        ? (() => {
            const raw =
              fields["Processed Areas of Interest (from Form Submissions)"];
            if (Array.isArray(raw)) {
              return parseProcessedFocusAreas(raw).join(", ");
            }
            if (typeof raw === "string" && raw.trim()) {
              return parseProcessedFocusAreas(raw).join(", ");
            }
            return "";
          })()
        : "",
    areasOfInterestFromForm:
      tableName === "Patients"
        ? Array.isArray(fields["Areas of Interest (from Form Submissions)"])
          ? fields["Areas of Interest (from Form Submissions)"].join(", ")
          : typeof fields["Areas of Interest (from Form Submissions)"] ===
              "string"
            ? fields["Areas of Interest (from Form Submissions)"]
            : ""
        : "",
    archived: fields["Archived"] || false,
    offerClaimed: fields["Offer Claimed"] || false,
    offerEarned:
      fields["Offer Earned"] === undefined
        ? undefined
        : Boolean(fields["Offer Earned"]),
    offerExpirationDate:
      fields["Offer Expiration"] ||
      fields["Offer Expiration Date"] ||
      fields["Coupon Expiration"] ||
      null,
    locationName:
      tableName === "Patients"
        ? fields[
            "Location name (from Boulevard Appointments) (from Form Submissions)"
          ] || null
        : null,
    appointmentStaffName: (() => {
      if (tableName !== "Patients") return null;
      const first =
        fields[
          "Appointment Service Staff First Name (from Boulevard Appointments) (from Form Submissions)"
        ];
      const last =
        fields[
          "Appointment Service Staff Last Name (from Boulevard Appointments) (from Form Submissions)"
        ];
      const parts = [first, last].filter(Boolean);
      return parts.length ? parts.join(" ").trim() : null;
    })(),
    discussedItems: (() => {
      const raw =
        fields["Treatments Discussed"] ?? fields["Discussed Treatments"];
      if (!raw || typeof raw !== "string") return undefined;
      try {
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) return undefined;
        return (parsed as DiscussedItem[])
          .filter(
            (x) => x && typeof x.treatment === "string" && x.treatment.trim(),
          )
          .map((x, i) => ({ ...x, id: x.id || `disc-${record.id}-${i}` }));
      } catch {
        return undefined;
      }
    })(),
    contactHistory: [],
    skincareQuiz: parseSkincareQuizFromFields(fields as Record<string, unknown>),
    wellnessQuiz: parseWellnessQuizFromFields(fields as Record<string, unknown>),
    severityScoresFromAnalyses: parseSeverityScoresFromFields(
      fields as Record<string, unknown>,
    ),
    turntableVideoUrl:
      tableName === "Patients"
        ? ((fields["Turntable Video URL"] as string | null | undefined) || null)
        : null,
  };

  return client;
}
